import {FormEvent, useMemo, useState} from "react";
import {Badge, Button, Form, InputGroup, Modal, Table} from "react-bootstrap";
import {Link, useNavigate} from "react-router";
import {LicensesApi} from "../../api/LicensesApi";
import {UsersApi} from "../../api/UsersApi";
import {Editions, UserViewModel} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {SubmitButton} from "../../components/SubmitButton";
import {formatDate} from "../../components/format";

// TSA-1 licences: today every licence is a TSA-1 licence (the API's licences have no product field).
export function LicensesPage() {
    const navigate = useNavigate();
    const {data, error, loading} = useAsync(() => Promise.all([LicensesApi.getAll(), UsersApi.getAll()]), []);
    const [filter, setFilter] = useState('');
    const [creating, setCreating] = useState(false);
    const [licenses, users] = data ?? [];

    const userNames = useMemo(() => new Map(users?.map(x => [x.username, x.displayName]) ?? []), [users]);
    const shown = useMemo(() => {
        const text = filter.trim().toLowerCase();
        return (licenses ?? [])
            .filter(x => !text || [x.key, x.licensee, x.edition, x.ownerUsername, userNames.get(x.ownerUsername) ?? '']
                .some(field => field.toLowerCase().includes(text)))
            .sort((l, r) => r.createdOn.localeCompare(l.createdOn));
    }, [licenses, filter, userNames]);

    return (
        <>
            <PageHeader title="TSA-1 licences">
                <Button onClick={() => setCreating(true)} disabled={!users}><i className="bi bi-plus-lg me-1"></i>New licence</Button>
            </PageHeader>
            <ErrorAlert error={error}/>
            {loading && !data && <LoadingSpinner/>}
            {licenses && (
                <>
                    <InputGroup className="mb-3" style={{ maxWidth: '24rem' }}>
                        <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                        <Form.Control placeholder="Filter by key, licensee or owner" value={filter} onChange={e => setFilter(e.target.value)} aria-label="Filter"/>
                    </InputGroup>
                    <Table responsive hover>
                        <thead>
                        <tr><th>Key</th><th>Licensee</th><th>Edition</th><th>Owner</th><th>Seats</th><th>Created</th><th></th></tr>
                        </thead>
                        <tbody>
                        {shown.map(license => (
                            <tr key={license.id} role="button" onClick={() => navigate(`/admin/licenses/${license.id}`)}>
                                <td className="license-key">{license.key}</td>
                                <td>{license.licensee}</td>
                                <td>{license.edition}</td>
                                <td>{license.ownerUsername ? (userNames.get(license.ownerUsername) ?? license.ownerUsername) : <span className="text-body-secondary">None</span>}</td>
                                <td>{license.activations.length} / {license.seats}</td>
                                <td>{formatDate(license.createdOn)}</td>
                                <td className="text-end">
                                    {license.revoked && <Badge bg="danger" className="me-2">Revoked</Badge>}
                                    <Link to={`/admin/licenses/${license.id}`} onClick={e => e.stopPropagation()}>Open</Link>
                                </td>
                            </tr>
                        ))}
                        {shown.length === 0 && <tr><td colSpan={7} className="text-center text-body-secondary py-4">No licences{filter && ' match the filter'}.</td></tr>}
                        </tbody>
                    </Table>
                </>
            )}
            {users && <CreateLicenseModal show={creating} users={users} onHide={() => setCreating(false)}
                                          onCreated={id => navigate(`/admin/licenses/${id}`)}/>}
        </>
    );
}

export function OwnerSelect({users, value, onChange, id}: { users: UserViewModel[], value: string, onChange(value: string): void, id: string }) {
    const sorted = [...users].sort((l, r) => l.displayName.localeCompare(r.displayName));
    return (
        <Form.Select id={id} value={value} onChange={e => onChange(e.target.value)}>
            <option value="">No owner</option>
            {sorted.map(x => <option key={x.username} value={x.username}>{x.displayName} ({x.username})</option>)}
            {value && !users.some(x => x.username === value) && <option value={value}>{value} (no such user)</option>}
        </Form.Select>
    );
}

function CreateLicenseModal({show, users, onHide, onCreated}: { show: boolean, users: UserViewModel[], onHide(): void, onCreated(id: string): void }) {
    const [licensee, setLicensee] = useState('');
    const [edition, setEdition] = useState(Editions[0]);
    const [owner, setOwner] = useState('');
    const [key, setKey] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    // Picking an owner fills in the licensee, until it's typed by hand.
    const pickOwner = (username: string) => {
        const previous = users.find(x => x.username === owner)?.displayName ?? '';
        if (!licensee || licensee === previous) {
            setLicensee(users.find(x => x.username === username)?.displayName ?? '');
        }
        setOwner(username);
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!licensee.trim()) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            const created = await LicensesApi.create({
                licensee: licensee.trim(),
                edition,
                ownerUsername: owner,
                revoked: false,
                key: key.trim() || undefined
            });
            onCreated(created.id);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton><Modal.Title>New TSA-1 licence</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} messages={{ 409: 'A licence with that key already exists.' }}/>
                    <Form.Group className="mb-3" controlId="new-license-owner">
                        <Form.Label>Owner</Form.Label>
                        <OwnerSelect id="new-license-owner" users={users} value={owner} onChange={pickOwner}/>
                        <Form.Text>The owner sees the licence under My licences and can free its seats.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="new-license-licensee">
                        <Form.Label>Licensee</Form.Label>
                        <Form.Control value={licensee} onChange={e => setLicensee(e.target.value)} isInvalid={validated && !licensee.trim()}/>
                        <Form.Text>Shown in TSA-1's licence dialog.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="new-license-edition">
                        <Form.Label>Edition</Form.Label>
                        <Form.Select value={edition} onChange={e => setEdition(e.target.value)}>
                            {Editions.map(x => <option key={x}>{x}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group controlId="new-license-key">
                        <Form.Label>Key <span className="text-body-secondary">(optional)</span></Form.Label>
                        <Form.Control className="license-key" placeholder="Generated if left empty" value={key} onChange={e => setKey(e.target.value)}/>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Create licence</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
